/*
 * Sourced from https://v0.dev/chat/menu-navbar-sidebar-w-o-localization-Y0lZuopJCUv
 * See docs/v0.md for more
 */

'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'

import type { NavbarCopy } from '@/lib/app-shell/copy'
import { renderClientTemplate } from '@/lib/page-tree/client-template'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { useSession } from '@/hooks/use-session'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import { Link } from '@/i18n/navigation'
import { trackNavClickEvent } from '@/services/auth/client/auth-events'

import { CurrencyMenu, type CurrencyOption } from './navigation/currency-menu'
import { DropdownCaret } from './navigation/dropdown-caret'
import { LocaleMenu } from './navigation/locale-menu'
import type { LocaleOption } from './navigation/locale-menu-config'

export interface MenuItem {
    title: string
    shortTitle?: string
    url: string
    /** Another application serves this destination — render it as a plain anchor. */
    native?: boolean
    description?: string
    icon?: React.ReactNode
    items?: MenuItem[]
}

export interface NavbarLogo {
    url: string
    src: string
    alt: string
    width?: number
    height?: number
}

/** One account-menu entry: its label, where it goes, and who serves it. */
interface AuthLink {
    title: string
    url: string
    /** Another application serves this destination — render it as a plain anchor. */
    native?: boolean
}

export interface NavbarAuth {
    login: AuthLink
    signup: AuthLink
    myTrips: AuthLink
    account: AuthLink
    points: AuthLink
}

export interface NavbarProps {
    copy: NavbarCopy
    logo: NavbarLogo
    menu: MenuItem[]
    auth?: NavbarAuth
    /**
     * The countries the header offers, from `buildLocaleOptions`. Omitted (or fewer
     * than two) renders no picker — the same config gate the account menu uses.
     */
    localeOptions?: LocaleOption[]

    /** The currencies to offer; omitted renders no currency picker. */
    currencyOptions?: CurrencyOption[]
    /** Optional brand element rendered beside the logo (e.g. the blog masthead). */
    brandSlot?: React.ReactNode
    /** When set with `brandSlot`, wraps the slot in a link to this URL. */
    brandUrl?: string
}

// The accent is keyed to `:focus-visible`, not `:focus`. A pointer press focuses the
// anchor and the browser keeps it focused — through the navigation the press starts,
// and again when a back navigation restores this page from the back/forward cache — so
// a highlight on `:focus` alone outlives the press and reads as a selected entry on a
// page the entry does not name. The pointer already has its own feedback in `:hover`.
const navLinkStyle =
    'inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1'

export const Navbar = ({
    copy,
    logo,
    menu,
    auth,
    localeOptions,
    currencyOptions,
    brandSlot,
    brandUrl,
}: NavbarProps) => {
    const { session, logout } = useSession()
    const isLoggedIn = session.isAuthenticated
    const firstName = session.firstName ?? ''
    const { toggleSidebar, setOpenMobile } = useSidebar()
    const isMobile = useIsMobile()
    // An empty list is a surface that offers no currency, not a picker with nothing in
    // it — the same reading the country picker gives its own empty list.
    const hasCurrencyPicker = currencyOptions !== undefined && currencyOptions.length > 0
    // Which menu entry's dropdown is showing. Load-bearing as state rather than a
    // `:hover` / `:focus-within` reveal: the entries are routed, so choosing one
    // replaces no document, and both conditions would still hold on the page that
    // arrives — the pointer resting on the link the visitor pressed, the browser
    // leaving that link focused — so the panel would follow them there.
    const [openEntry, setOpenEntry] = useState<string | null>(null)

    // Close sidebar when switching from mobile to desktop
    useEffect(() => {
        if (!isMobile) {
            setOpenMobile(false)
        }
    }, [isMobile, setOpenMobile])

    return (
        <header className="sticky top-0 z-30 w-full border-b bg-background lg:static">
            <div className="mx-auto flex h-[50px] w-full items-center justify-between px-5 lg:h-[70px] lg:w-[980px] lg:px-0">
                <div className="flex items-center gap-4 max-sm:min-w-0">
                    <Link
                        href={logo.url}
                        className="flex items-center rounded-md focus:outline-none"
                    >
                        <Image
                            priority
                            src={logo.src}
                            alt={logo.alt}
                            width={logo.width}
                            height={logo.height}
                            className="h-8 w-auto lg:h-10"
                        />
                    </Link>

                    {/* Optional brand element beside the logo (blog masthead),
                        wrapped in a link when a brand URL is supplied. */}
                    {brandSlot &&
                        (brandUrl ? (
                            <a
                                href={brandUrl}
                                className="border-l border-gray-300 pl-5 max-sm:min-w-0"
                            >
                                {brandSlot}
                            </a>
                        ) : (
                            brandSlot
                        ))}

                    <nav className="hidden lg:flex" aria-label={copy.mainNavigation.text}>
                        <ul className="flex list-none items-center gap-1">
                            {menu.map((item) =>
                                item.items ? (
                                    <li
                                        key={item.title}
                                        className="relative"
                                        onMouseEnter={() => setOpenEntry(item.title)}
                                        onMouseLeave={(event) => {
                                            // Keyboard focus inside the panel outlives
                                            // the pointer. Hiding it here would leave
                                            // the focused link `visibility: hidden`,
                                            // which a browser refuses to focus — the
                                            // tab sequence would stop dead here.
                                            if (
                                                !event.currentTarget.contains(
                                                    document.activeElement,
                                                )
                                            ) {
                                                setOpenEntry(null)
                                            }
                                        }}
                                        onFocus={() => setOpenEntry(item.title)}
                                        onBlur={(event) => {
                                            // `relatedTarget` names the element about to
                                            // take focus: moving between the entry and
                                            // its own destinations must not hide the
                                            // panel out from under the focus that is
                                            // travelling into it.
                                            if (
                                                !event.currentTarget.contains(
                                                    event.relatedTarget as Node | null,
                                                )
                                            ) {
                                                setOpenEntry(null)
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Escape') {
                                                setOpenEntry(null)
                                            }
                                        }}
                                    >
                                        {/* No caret, matching the legacy header: the
                                            dropdown opens on hover and focus, but the
                                            row reads as a link rather than a control. */}
                                        <Link
                                            href={item.url}
                                            native={item.native}
                                            className={navLinkStyle}
                                            onClick={() => setOpenEntry(null)}
                                        >
                                            {item.title}
                                        </Link>
                                        <div
                                            data-slot="nav-dropdown"
                                            data-state={
                                                openEntry === item.title ? 'open' : 'closed'
                                            }
                                            className={cn(
                                                "absolute top-full left-0 z-50 mt-1.5 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow transition-all duration-200 before:absolute before:-top-2 before:left-0 before:h-2 before:w-full before:content-['']",
                                                openEntry === item.title
                                                    ? 'visible opacity-100'
                                                    : 'invisible opacity-0',
                                            )}
                                        >
                                            <div className="grid gap-1">
                                                {item.items.map((subItem) => (
                                                    <Link
                                                        key={subItem.url}
                                                        href={subItem.url}
                                                        native={subItem.native}
                                                        // The panel is in the DOM on every
                                                        // page — hidden by opacity, not by
                                                        // absence — so a routed sub-item
                                                        // would speculatively fetch its
                                                        // route from every page on the site.
                                                        prefetch={false}
                                                        onClick={() => setOpenEntry(null)}
                                                        className="block rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                                                    >
                                                        {subItem.title}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </li>
                                ) : (
                                    <li key={item.title}>
                                        <Link
                                            href={item.url}
                                            native={item.native}
                                            className={navLinkStyle}
                                        >
                                            {item.title}
                                        </Link>
                                    </li>
                                ),
                            )}
                        </ul>
                    </nav>
                </div>

                {/* Right side: the country picker, the currency picker, then the account
                    menu — ui-react's own header order. Each is optional and gated
                    independently, so this renders if any is supplied.

                    The rule between the two pickers is ui-react's `.menu:first-child`
                    border, reproduced here rather than approximated: the pickers read as
                    one group, set apart from the account control, which is why the gap to
                    the account menu is wider than the gap within the pair. It is drawn on
                    the currency picker rather than between them so it disappears with it,
                    leaving no rule hanging beside a lone flag. */}
                {(localeOptions !== undefined || hasCurrencyPicker || auth !== undefined) && (
                    <div className="hidden items-center lg:flex">
                        {localeOptions && (
                            <LocaleMenu options={localeOptions} label={copy.chooseCountry} />
                        )}
                        {hasCurrencyPicker && (
                            <div
                                className={cn(
                                    'pl-[15px]',
                                    localeOptions && 'ml-[15px] border-l border-[#bbb]',
                                )}
                            >
                                <CurrencyMenu options={currencyOptions} label={copy.menuLabel} />
                            </div>
                        )}
                        {/* Uncontrolled: the primitive owns its open state, which is what
                            makes the menu explicit-activation only — click/tap and
                            Enter/Space/ArrowDown open it, Escape and an outside press close
                            it. Do not layer a hover-open listener over it: `mouseenter`
                            fires before the trigger's own `pointerdown`, so a hover listener
                            puts click out of phase and the press that follows toggles the
                            menu straight back shut (issue #494). */}
                        {auth && (
                            // ui-react sets the pickers apart from the account control with
                            // a wider gap than the one inside the pair (`.accountMenu`).
                            <div className="ml-10">
                                <DropdownMenu modal={false}>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="group flex min-w-[96px] items-center justify-center gap-1 text-sm font-medium text-foreground hover:bg-transparent"
                                        >
                                            {/* SSR/SSG renders logged-out (session-blind, cache-safe);
                                        after hydration useSession resolves the mirror and the
                                        control re-renders. The min-width keeps the swap from
                                        shifting layout (CLS). */}
                                            {isLoggedIn ? (
                                                <span>
                                                    {firstName
                                                        ? renderClientTemplate(copy.greeting, {
                                                              name: firstName,
                                                          })
                                                        : copy.myAccount.text}
                                                </span>
                                            ) : (
                                                <span>{copy.logIn.text}</span>
                                            )}
                                            <DropdownCaret className="transition duration-300 group-data-[state=open]:rotate-180" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-72">
                                        <div className="p-6">
                                            {/* Logged-out CTAs. Account links below render in both
                                        states (ui-react AccountMenuContent parity). */}
                                            {!isLoggedIn && (
                                                <div className="space-y-2.5">
                                                    <Button
                                                        asChild
                                                        variant="strong"
                                                        size="lg"
                                                        className="w-full font-bold tracking-[2px] uppercase"
                                                    >
                                                        <Link
                                                            href={auth.signup.url}
                                                            native={auth.signup.native}
                                                        >
                                                            {auth.signup.title}
                                                        </Link>
                                                    </Button>
                                                    <Button
                                                        asChild
                                                        variant="strong-outline"
                                                        size="lg"
                                                        className="w-full font-bold tracking-[2px] uppercase"
                                                    >
                                                        <Link
                                                            href={auth.login.url}
                                                            native={auth.login.native}
                                                            onClick={() =>
                                                                trackNavClickEvent({
                                                                    label: 'log_in',
                                                                    navType: 'nav_bar',
                                                                })
                                                            }
                                                        >
                                                            {auth.login.title}
                                                        </Link>
                                                    </Button>
                                                </div>
                                            )}
                                            <div
                                                className={cn(
                                                    'flex flex-col',
                                                    !isLoggedIn && 'mt-6',
                                                )}
                                            >
                                                <DropdownMenuItem asChild>
                                                    <Link
                                                        href={auth.myTrips.url}
                                                        native={auth.myTrips.native}
                                                        className="cursor-pointer py-3 text-sm focus:bg-muted"
                                                    >
                                                        {auth.myTrips.title}
                                                    </Link>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem asChild>
                                                    <Link
                                                        href={auth.account.url}
                                                        native={auth.account.native}
                                                        className="cursor-pointer py-3 text-sm focus:bg-muted"
                                                    >
                                                        {auth.account.title}
                                                    </Link>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem asChild>
                                                    <Link
                                                        href={auth.points.url}
                                                        native={auth.points.native}
                                                        className="cursor-pointer py-3 text-sm focus:bg-muted"
                                                    >
                                                        {auth.points.title}
                                                    </Link>
                                                </DropdownMenuItem>
                                                {isLoggedIn && (
                                                    <>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            className="cursor-pointer py-3 text-sm focus:bg-muted"
                                                            onSelect={() => {
                                                                trackNavClickEvent({
                                                                    label: 'log_out',
                                                                    navType: 'nav_bar',
                                                                })
                                                                void logout()
                                                            }}
                                                        >
                                                            {copy.logOut.text}
                                                        </DropdownMenuItem>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex items-center lg:hidden">
                    <button
                        type="button"
                        className="flex h-5 w-[30px] flex-col justify-between"
                        onClick={toggleSidebar}
                        aria-label={copy.toggleSidebar.text}
                    >
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                    </button>
                </div>
            </div>
        </header>
    )
}
